import { ButtonBase, Stack } from "@mui/material"
import { colors } from "../../../services/config/colors"
import Text from "../../global/common/Text"
import Title from "../../global/common/Title"
import { motion, useReducedMotion } from "framer-motion"
import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react"
import { MissionsContext } from "../../../controllers/missions"
import { useTranslation } from "react-i18next"
import trophyWhiteIcon from "../../../assets/icons/trophy-white.svg"
import warningWhiteIcon from "../../../assets/icons/warning-white.svg"
import checkWhiteIcon from "../../../assets/icons/check-white.svg"
import PointsBadge from "../../global/common/PointsBadge"
import {
  MissionPeriod,
  MissionSubType,
  MissionType,
} from "../../../services/config/enum"
import { JourneysContext } from "../../../controllers/journeys"
import { ActionsContext } from "../../../controllers/actions"
import bellIcon from "../../../assets/icons/bell.svg"
import {
  addDays,
  daysBetweenDates,
  hoursUntilMidnight,
} from "../../../services/utils/utils"
import { MainContext } from "../../../controllers/main"
import Confetti from "../../global/common/Confetti"
import TimeBadge from "../../global/common/TimeBadge"
import lockWhiteIcon from "../../../assets/icons/lock-white.svg"
import moonWhiteIcon from "../../../assets/icons/moon-white.png"
import Mission from "../../../models/mission"
import BoostBadge from "../../missions/common/BoostBadge"

const MissionCardMobile = ({
  mission,
  index,
  slidesLength,
  color,
  title,
  titleColor = colors.text,
  goButtonColor,
  image,
  current,
  target,
  points,
  satisfied,
  missionType,
  missionSubType,
  missionTypeId,
  period,
  setBottomSheetOpen,
  onClick,
  end,
  currentPerDay,
  targetPerDay,
  setComeBackAlertOpen,
  setCarouselControlsHidden,
  updatedAt,
  isCorrect,
}: {
  mission: Mission
  index: number
  slidesLength: number
  color: string
  title: string
  titleColor?: string
  goButtonColor: string
  image: string
  current: number
  target: number
  points: number
  satisfied: boolean
  missionType: MissionType
  missionSubType: MissionSubType
  missionTypeId: string
  period: MissionPeriod
  setBottomSheetOpen: Dispatch<SetStateAction<boolean>>
  onClick: () => void
  end?: string
  currentPerDay?: number
  targetPerDay?: number
  setComeBackAlertOpen: Dispatch<SetStateAction<boolean>>
  setCarouselControlsHidden: Dispatch<SetStateAction<boolean>>
  updatedAt?: string
  isCorrect?: boolean
}) => {
  const { t } = useTranslation()
  const shouldReduceMotion = useReducedMotion()
  const { viewTutorial, currentTutorialPage } = useContext(MainContext)
  const { currentSlide, missionsLocked, missionsHistory } =
    useContext(MissionsContext)
  const { journeys, dailyEpisode } = useContext(JourneysContext)
  const { actions } = useContext(ActionsContext)

  // animation
  const [cardOpen, setCardOpen] = useState<boolean>(false)

  // mission from history (for boost missions)
  const missionFromHistory = useMemo(
    () => missionsHistory.find((item) => item.missionId === mission.missionId),
    [mission, missionsHistory]
  )

  return (
    <motion.div
      style={{
        width: 256,
        height: "100%",
        maxHeight: 340,
        display: "flex",
        justifyContent: "center",
        position: "relative",
      }}
      onClick={() => {
        if (
          Math.abs(currentSlide % slidesLength) === index &&
          !satisfied &&
          !missionsLocked
        ) {
          if (
            (currentPerDay &&
              targetPerDay &&
              currentPerDay >= targetPerDay &&
              updatedAt &&
              new Date(updatedAt).toLocaleDateString() ===
                new Date().toLocaleDateString()) ||
            (missionType === MissionType.LEARN &&
              current < target &&
              updatedAt &&
              new Date(updatedAt).toLocaleDateString() ===
                new Date().toLocaleDateString())
          ) {
            setComeBackAlertOpen(true)
            return
          }

          if (shouldReduceMotion) {
            onClick()
          } else {
            setBottomSheetOpen(false)
            setCarouselControlsHidden(true)
            setCardOpen(true)
            setTimeout(() => {
              onClick()
            }, 220)
          }
        }
      }}
      animate={cardOpen ? "open" : "closed"}
      variants={{
        open: { scale: 5 },
        closed: { scale: 1 },
      }}
      transition={{ scale: { type: "spring", duration: 0.5 } }}
      initial={false}
      aria-hidden={
        Math.abs(currentSlide % slidesLength) === index ? "false" : "true"
      }
    >
      {missionsLocked ? (
        <Stack
          alignItems="center"
          style={{
            width: "100%",
            height: "calc(100% - 23px)",
            borderRadius: 20,
            backgroundColor: color,
            paddingTop: 22,
            paddingInline: 16,
            position: "relative",
            boxShadow: "0px 4.8px 19px rgba(22, 59, 99, 0.20)",
          }}
        >
          <img src={moonWhiteIcon} style={{ height: 40 }} alt="" />
          <Title
            fontSize={18}
            lineHeight="21px"
            color={colors.textWhite}
            textAlign="center"
            style={{ marginTop: 18, textTransform: "uppercase" }}
          >
            Missione {missionType} non disponibile
          </Title>
          <Text
            fontSize={11}
            fontWeight={500}
            color={colors.textWhite}
            style={{ textTransform: "uppercase", marginTop: 28 }}
          >
            Sblocca alle:
          </Text>
          <TimeBadge />
          <Text
            fontSize={14}
            lineHeight="16px"
            fontWeight={400}
            textAlign="center"
            color={colors.textWhite}
            style={{ position: "absolute", bottom: 31, fontStyle: "italic" }}
            paddingLeft={32}
            paddingRight={32}
          >
            Torna più tardi per{" "}
            {missionType === MissionType.ACT
              ? "registrare nuove azioni sostenibili"
              : missionType === MissionType.LEARN
              ? "leggere nuovi episodi"
              : "rispondere a nuove domande"}
          </Text>
        </Stack>
      ) : satisfied ? (
        <Stack
          alignItems="center"
          style={{
            width: "100%",
            height: "calc(100% - 23px)",
            borderRadius: 20,
            backgroundColor: color,
            paddingTop: 22,
            paddingInline: 16,
            position: "relative",
            boxShadow: "0px 4.8px 19px rgba(22, 59, 99, 0.20)",
          }}
        >
          <img
            src={isCorrect === false ? warningWhiteIcon : trophyWhiteIcon}
            style={{ height: 40 }}
            alt=""
          />
          <Title
            fontSize={18}
            lineHeight="21px"
            color={colors.textWhite}
            textAlign="center"
            style={{ marginTop: 18 }}
          >
            {isCorrect === false
              ? t("mission_not_completed").toUpperCase() + "!"
              : t("mission_completed").toUpperCase() + "!"}
          </Title>
          <Text
            fontSize={16}
            fontWeight={500}
            lineHeight="19px"
            color={colors.textWhite}
            textAlign="center"
            style={{ width: "100%", marginTop: 8, fontStyle: "italic" }}
            paddingLeft={16}
            paddingRight={16}
          >
            {title}
          </Text>
          {isCorrect !== false && (
            <Stack
              direction="row"
              gap={1}
              style={{ height: 32, position: "absolute", bottom: 91 }}
            >
              <PointsBadge
                points={
                  missionFromHistory && missionFromHistory.boost
                    ? missionFromHistory.points
                    : points
                }
              />
              {missionFromHistory && missionFromHistory.boost ? (
                <BoostBadge />
              ) : null}
            </Stack>
          )}
          <Text
            fontSize={14}
            lineHeight="16px"
            fontWeight={400}
            textAlign="center"
            color={colors.textWhite}
            style={{ position: "absolute", bottom: 31 }}
            paddingLeft={32}
            paddingRight={32}
          >
            {period === MissionPeriod.DAILY
              ? t("come_back_tomorrow")
              : period === MissionPeriod.WEEKLY
              ? t("come_back_next_week")
              : period === MissionPeriod.CUSTOM && end
              ? t("come_back_next_month", {
                  date: addDays(new Date(end), 1).toLocaleDateString(),
                })
              : null}
          </Text>
        </Stack>
      ) : (
        <div
          style={{
            width: "100%",
            height: "calc(100% - 23px)",
            borderRadius: 20,
            backgroundColor: colors.backgroundWhite,
          }}
        >
          <ButtonBase
            disabled={
              Math.abs(currentSlide % slidesLength) !== index ? true : false
            }
            style={{
              width: "100%",
              height: "100%",
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              borderRadius: 20,
              boxShadow: "0px 4.8px 19px rgba(22, 59, 99, 0.20)",
              opacity: cardOpen ? 0 : 1,
              transition: "200ms",
              position: "relative",
            }}
            aria-label={t("go_to_specific_mission", { mission: title })}
          >
            <Stack
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 20,
                paddingInline: 21,
                position: "relative",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                style={{
                  width: "auto",
                  height: 28,
                  position: "absolute",
                  top: 19.5,
                  right: 21,
                  gap: 10,
                }}
              >
                {/* period badge */}
                {mission.boost ? (
                  <BoostBadge />
                ) : (period === MissionPeriod.CUSTOM &&
                    end &&
                    daysBetweenDates(new Date(), new Date(end), true) <= 0) ||
                  mission.boost ? null : (
                  <Stack
                    direction="row"
                    alignItems="center"
                    style={{
                      width: "auto",
                      height: "100%",
                      paddingInline: 8,
                      backgroundColor: colors.backgroundWhite,
                      borderRadius: 4,
                      border: "1px solid " + colors.tertiary,
                      gap: 6,
                    }}
                  >
                    {period === MissionPeriod.DAILY ? (
                      <img src={bellIcon} style={{ height: 14 }} alt="" />
                    ) : null}
                    {period === MissionPeriod.DAILY ? (
                      <Title
                        fontSize={14}
                        color={colors.tertiary}
                        style={{ marginTop: 4 }}
                      >
                        {`-${hoursUntilMidnight()} ${t("hours")}`}
                      </Title>
                    ) : period === MissionPeriod.WEEKLY ? (
                      <Title
                        fontSize={14}
                        color={colors.tertiary}
                        style={{ marginTop: 4 }}
                      >
                        {t(period.toLowerCase())}
                      </Title>
                    ) : period === MissionPeriod.CUSTOM && end ? (
                      <Title
                        fontSize={14}
                        color={colors.tertiary}
                        style={{ marginTop: 4 }}
                      >
                        -{daysBetweenDates(new Date(), new Date(end), true)}{" "}
                        {t("days")}
                      </Title>
                    ) : null}
                  </Stack>
                )}
                {/* points badge */}
                <PointsBadge
                  points={points}
                  style={{
                    height: "100%",
                  }}
                />
              </Stack>
              <Title
                fontSize={16}
                lineHeight="20px"
                color={titleColor}
                style={{
                  width:
                    missionSubType === MissionSubType.DAILYEPISODE &&
                    period === MissionPeriod.DAILY
                      ? "100%"
                      : "85%",
                  marginTop: 64,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {title}
              </Title>
              {/* journey */}
              {missionSubType === MissionSubType.JOURNEY && (
                <div
                  style={{
                    width: "56%",
                    height: 37,
                    display: "flex",
                    alignItems: "center",
                    marginTop: 16,
                  }}
                >
                  <Title
                    fontSize={15}
                    lineHeight="18px"
                    color={colors.textWhite}
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {
                      journeys.find((journey) => journey.id === missionTypeId)!
                        .title
                    }
                  </Title>
                </div>
              )}
              {missionSubType === MissionSubType.JOURNEY && (
                <div
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: 18,
                    backgroundColor: color,
                    position: "absolute",
                    right: 66,
                    top: 155,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={
                      journeys.find((journey) => journey.id === missionTypeId)!
                        .image
                    }
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: 14,
                      objectFit: "cover",
                    }}
                    alt=""
                  />
                </div>
              )}
              {missionSubType === MissionSubType.JOURNEY && (
                <Stack
                  style={{
                    width: 30,
                    position: "absolute",
                    top: 94.5,
                    right: 12,
                    gap: 10,
                  }}
                >
                  {journeys
                    .find((journey) => journey.id === missionTypeId)!
                    .episodes.slice(0, 5)
                    .map((episode) => (
                      <img
                        key={episode.id}
                        src={episode.image}
                        style={{
                          width: 30,
                          height: 30,
                          minHeight: 30,
                          borderRadius: 6,
                        }}
                        alt={t("episode")}
                      />
                    ))}
                </Stack>
              )}
              {/* daily episode daily */}
              {missionSubType === MissionSubType.DAILYEPISODE &&
              period === MissionPeriod.DAILY &&
              dailyEpisode ? (
                <Stack
                  alignItems="center"
                  style={{
                    width: "100%",
                    position: "absolute",
                    top: 118,
                    left: 0,
                    gap: 6,
                  }}
                >
                  <img
                    src={dailyEpisode?.image}
                    style={{
                      width: 100,
                      height: 90,
                      borderRadius: 14,
                      objectFit: "cover",
                    }}
                    alt=""
                  />
                  <Title
                    fontSize={15}
                    lineHeight="18px"
                    color={colors.textWhite}
                    textAlign="center"
                    style={{
                      width: "55%",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {dailyEpisode?.title}
                  </Title>
                </Stack>
              ) : null}
              {/* daily episode weekly and custom */}
              {missionSubType === MissionSubType.DAILYEPISODE &&
              (period === MissionPeriod.WEEKLY ||
                period === MissionPeriod.CUSTOM) &&
              dailyEpisode ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  style={{
                    width: "calc(100% - 60px)",
                    height: 120,
                    position: "absolute",
                    top: 130,
                    gap: 8,
                  }}
                >
                  <img
                    src={dailyEpisode?.image}
                    style={{
                      width: "60%",
                      height: "100%",
                      borderRadius: 14,
                      objectFit: "cover",
                    }}
                    alt=""
                  />
                  <Stack style={{ width: "40%", height: "100%", gap: 8 }}>
                    <div
                      style={{
                        width: "100%",
                        height: "calc(50% - 4px)",
                        borderRadius: 14,
                        backgroundImage: `url(${
                          dailyEpisode?.slides.find(
                            (slide) => slide.slideType === "TitleSlide"
                          )?.background
                        })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <img
                        src={
                          (dailyEpisode?.slides as any[]).filter(
                            (item) => item.image
                          )[0].image.url
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: 14,
                          objectFit: "cover",
                        }}
                        alt=""
                      />
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "calc(50% - 4px)",
                        borderRadius: 14,
                        backgroundImage: `url(${
                          dailyEpisode?.slides.find(
                            (slide) => slide.slideType === "TitleSlide"
                          )?.background
                        })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      <img
                        src={
                          (dailyEpisode?.slides as any[]).filter(
                            (item) => item.image
                          )[1] &&
                          (dailyEpisode?.slides as any[]).filter(
                            (item) => item.image
                          )[1].image
                            ? (dailyEpisode?.slides as any[]).filter(
                                (item) => item.image
                              )[1].image.url
                            : dailyEpisode!.image
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: 14,
                          objectFit: "cover",
                        }}
                        alt=""
                      />
                    </div>
                  </Stack>
                </Stack>
              ) : null}
              {/* single action */}
              {actions.some((action) => action.id === missionTypeId) && (
                <img
                  src={
                    actions.find((action) => action.id === missionTypeId)?.image
                  }
                  style={{
                    width: 160,
                    height: 160,
                    objectFit: "cover",
                    position: "absolute",
                    top: 110,
                    left: 0,
                    right: 0,
                    marginInline: "auto",
                  }}
                  alt=""
                />
              )}
            </Stack>
            {/* go button */}
            <Title
              fontSize={14}
              color={
                goButtonColor === "#FFFFFF"
                  ? goButtonColor
                  : (colors.missions as any)[missionType.toLowerCase()]
              }
              style={{
                position: "absolute",
                bottom: 30,
                textDecoration: "underline",
              }}
            >
              {t(missionType.toLowerCase()).toUpperCase()}
            </Title>
          </ButtonBase>
        </div>
      )}
      {/* completion dot */}
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: "100%",
          backgroundColor:
            missionsLocked || satisfied ? color : colors.backgroundWhite,
          border:
            "2px solid " +
            (missionsLocked || satisfied ? colors.backgroundWhite : color),
          position: "absolute",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform:
            viewTutorial && currentTutorialPage === 4 && index === 2
              ? "scale(1.5)"
              : "none",
          transition: "400ms",
          transitionTimingFunction:
            viewTutorial && currentTutorialPage === 4 && index === 2
              ? "cubic-bezier(.17, .67, .41, 2)"
              : "ease-out",
        }}
      >
        {viewTutorial && currentTutorialPage === 4 && index === 2 ? (
          <Confetti
            particleCount={60}
            width={window.innerWidth + 100}
            top={23}
            zIndex={150}
          />
        ) : null}
        {missionsLocked ? (
          <img
            src={lockWhiteIcon}
            style={{ width: 24 }}
            alt={t("mission_completed")}
          />
        ) : satisfied ? (
          <img
            src={checkWhiteIcon}
            style={{ width: 24, transform: "scale(1.1)" }}
            alt={t("mission_completed")}
          />
        ) : (
          <Text
            fontSize={14}
            lineHeight="19.6px"
            fontWeight={700}
            color={color}
            ariaLabel={t("mission_progression_count", {
              count: current,
              total: target,
            })}
          >
            {viewTutorial && currentTutorialPage === 4 && index === 2
              ? `${target}/${target}`
              : `${current}/${target}`}
          </Text>
        )}
      </div>
    </motion.div>
  )
}

export default MissionCardMobile
